import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

// The one button system. Replaces the per-panel btnStyle / btnGhostStyle /
// btnStrongStyle one-offs (which drifted on fill, padding and corner radius).
//
//   primary    the single main action — accent-filled, white label, weight 600
//   secondary  the default control — surface fill + hairline border (the
//              "white-with-a-border" look), primary label
//   ghost      low-emphasis / inline — borderless, secondary label
//
// Two sizes: `sm` (dense toolbars — the default) and `md` (a prominent primary
// action). Every button shares --radius-sm corners, the --t-body label, an icon
// gap and a uniform disabled treatment, so a toolbar reads as one family.

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md';

const base: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--s-1)',
  fontSize: 'var(--t-body)',
  lineHeight: 'var(--line-tight)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
};

// Vertical padding is tuned so a button matches the ~20px height of the native
// selects/inputs it shares a toolbar with (font --t-body + 2px pad + 1px border
// ≈ 20px). `md` is intentionally taller for a standalone lead action.
const sizeStyle: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '2px var(--s-2)' },
  md: { padding: '5px var(--s-3)', fontWeight: 600 },
};

const variantStyle: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--accent)',
    color: 'var(--menu-surface)',
    border: '1px solid var(--accent)',
    fontWeight: 600,
  },
  // The control surface is WHITE (--menu-surface) — same as selects/inputs — so a
  // secondary button reads as one family with the form controls beside it, not as
  // a cream blob on a cream panel.
  secondary: {
    background: 'var(--menu-surface)',
    color: 'var(--text-primary)',
    border: '1px solid var(--hairline)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid transparent',
  },
};

/** Raw style object for the rare call site that styles a non-<button> element
 *  (or merges into an existing style map). Prefer the <Button> component. */
export function buttonStyle(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'sm',
): CSSProperties {
  return { ...base, ...sizeStyle[size], ...variantStyle[variant] };
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon (e.g. a 12px lucide glyph) — rendered before the label. */
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'sm',
  icon,
  children,
  style,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      style={{
        ...buttonStyle(variant, size),
        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : null),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
